import { lockScroll, unlockScroll } from '@theme/utilities';

if (!customElements.get('see-in-your-size')) {
  const live = new Set();

  class SeeInYourSize extends HTMLElement {
    connectedCallback() {
      if (this.booted) {
        live.add(this);
        this.bindDocument();
        this.portalModal();
        return;
      }

      this.data = this.readPayload();
      if (!this.data) return;

      this.modal = this.querySelector('[data-sys-modal]');
      this.card = this.querySelector('[data-sys-card]');
      this.scrim = this.querySelector('[data-sys-scrim]');
      this.closeBtn = this.querySelector('[data-sys-close]');
      this.sizesEl = this.querySelector('[data-sys-sizes]');
      this.mediaEl = this.querySelector('[data-sys-media]');
      this.img = this.querySelector('[data-sys-img]');
      this.captionEl = this.querySelector('[data-sys-caption]');
      this.noteEl = this.querySelector('[data-sys-note]');
      this.cta = this.querySelector('[data-sys-cta]');

      if (!this.modal || !this.card || !this.sizesEl || !this.img || !this.cta) return;

      this.booted = true;
      this.isOpen = false;
      this.warmed = false;
      this.painted = false;
      this.sizeChanges = 0;
      this.swapToken = 0;
      this.current = null;
      this.applying = false;

      this.evictStaleTwins();

      this.onOpenerClick = (event) => this.handleOpenerClick(event);
      this.onClose = () => this.close();
      this.onKeydown = (event) => this.handleKeydown(event);
      this.onSizeClick = (event) => this.handleSizeClick(event);
      this.onCtaClick = () => this.handleCta();
      this.onSectionUnload = (event) => {
        if (event.detail && event.detail.sectionId === this.dataset.sectionId) this.remove();
      };
      this.onExternalChange = (event) => this.handleExternalChange(event);

      if (this.closeBtn) this.closeBtn.addEventListener('click', this.onClose);
      if (this.scrim) this.scrim.addEventListener('click', this.onClose);
      this.sizesEl.addEventListener('click', this.onSizeClick);
      this.cta.addEventListener('click', this.onCtaClick);

      live.add(this);
      this.bindDocument();
      this.portalModal();
    }

    disconnectedCallback() {
      this.teardown();
    }

    bindDocument() {
      document.addEventListener('click', this.onOpenerClick);
      document.addEventListener('shopify:section:unload', this.onSectionUnload);
      document.addEventListener('change', this.onExternalChange, true);
    }

    teardown() {
      if (!this.booted) return;
      live.delete(this);
      this.close(true);
      document.removeEventListener('click', this.onOpenerClick);
      document.removeEventListener('shopify:section:unload', this.onSectionUnload);
      document.removeEventListener('change', this.onExternalChange, true);
      document.removeEventListener('keydown', this.onKeydown, true);
      unlockScroll(this);
      if (this.modal && this.modal.isConnected && !this.contains(this.modal)) this.modal.remove();
    }

    readPayload() {
      const node = this.querySelector('script[data-sizeview-data]');
      if (!node) return null;
      try {
        const parsed = JSON.parse(node.textContent);
        if (!parsed || !Array.isArray(parsed.sizes) || !parsed.sizes.length) return null;
        if (!parsed.sizes.every((entry) => entry && entry.src)) return null;
        return parsed;
      } catch (error) {
        return null;
      }
    }

    evictStaleTwins() {
      const id = this.dataset.blockId;
      if (!id) return;
      document.querySelectorAll('see-in-your-size[data-block-id="' + id + '"]').forEach((twin) => {
        if (twin !== this) twin.remove();
      });
    }

    portalModal() {
      if (!this.modal) return;
      if (this.modal.parentNode === document.body) return;

      const uid = this.id || '';
      if (uid) {
        const key = uid.replace(/["\\]/g, '\\$&');
        document.querySelectorAll('.sys-modal[data-sys-portal="' + key + '"]').forEach((stale) => {
          if (stale !== this.modal) stale.remove();
        });
        this.modal.dataset.sysPortal = uid;
      }
      document.body.appendChild(this.modal);
    }

    openers() {
      return document.querySelectorAll('[data-sys-opens]');
    }

    setExpanded(state) {
      this.openers().forEach((node) => node.setAttribute('aria-expanded', state));
    }

    handleOpenerClick(event) {
      const node = event.target instanceof Element ? event.target : null;
      const opener = node ? node.closest('[data-sys-opens]') : null;
      if (!opener) return;
      const first = Array.from(live).filter((instance) => instance.isConnected)[0];
      if (first !== this) return;
      event.preventDefault();
      this.open(opener);
    }

    kachingRoot() {
      const pid = this.dataset.productId;
      if (pid) {
        const own = document.querySelector('kaching-bundle[product-id="' + String(pid).replace(/["\\]/g, '\\$&') + '"]');
        if (own) return own;
      }
      return document.querySelector('kaching-bundle');
    }

    variantPicker() {
      const pid = this.dataset.productId;
      if (!pid) return null;
      return document.querySelector('variant-picker[data-product-id="' + String(pid).replace(/["\\]/g, '\\$&') + '"]');
    }

    sizeValues() {
      if (!this.sizeSet) {
        this.sizeSet = {};
        this.data.sizes.forEach((entry) => {
          this.sizeSet[String(entry.value).trim()] = true;
        });
      }
      return this.sizeSet;
    }

    isSizeSelect(select) {
      const known = this.sizeValues();
      const options = select.options;
      if (!options || options.length === 0) return false;
      let hits = 0;
      for (let i = 0; i < options.length; i += 1) {
        if (known[String(options[i].value).trim()] === true) hits += 1;
      }
      return hits >= 2 && hits * 2 >= options.length;
    }

    kachingSizeSelects() {
      const root = this.kachingRoot();
      if (!root) return [];
      const bar = root.querySelector('.kaching-bundles__bar--selected') || root;
      const found = Array.prototype.slice
        .call(bar.querySelectorAll('select.kaching-bundles-option, select.kaching-bundles__bar-variant-select'))
        .filter((select) => this.isSizeSelect(select));
      if (found.length) return found;
      return Array.prototype.slice
        .call(root.querySelectorAll('select'))
        .filter((select) => this.isSizeSelect(select));
    }

    kachingOption(select, entry) {
      const options = select.options;
      const want = String(entry.value).trim();
      for (let i = 0; i < options.length; i += 1) {
        if (String(options[i].value).trim() === want) return options[i];
      }
      return null;
    }

    variantTarget(entry, picker) {
      const root = picker || this.variantPicker();
      if (!root || entry == null || entry.option_value_id == null) return null;
      const id = String(entry.option_value_id).replace(/["\\]/g, '\\$&');
      return root.querySelector('[data-option-value-id="' + id + '"]');
    }

    markSoldOut(chip, soldOut) {
      const label = (this.data.labels && this.data.labels.soldout) || '';
      chip.classList.toggle('sys-size--soldout', !!soldOut);

      let hidden = chip.querySelector('.sys-visually-hidden');
      if (soldOut && label) {
        if (!hidden) {
          hidden = document.createElement('span');
          hidden.className = 'sys-visually-hidden';
          chip.appendChild(hidden);
        }
        hidden.textContent = ' ' + label;
      } else if (hidden) {
        hidden.remove();
      }
    }

    nodeSoldOut(node) {
      if (node.tagName === 'OPTION') {
        if (node.disabled) return true;
        const shown = (node.textContent || '').trim();
        const raw = String(node.value == null ? '' : node.value).trim();
        return shown !== '' && raw !== '' && shown !== raw;
      }
      return node.getAttribute('data-option-available') === 'false' || node.getAttribute('aria-disabled') === 'true';
    }

    refreshAvailability() {
      const picker = this.variantPicker();
      const selects = this.kachingSizeSelects();
      const kaching = selects.length ? selects[0] : null;

      this.sizesEl.querySelectorAll('.sys-size').forEach((chip) => {
        const entry = this.entryFor(chip.dataset.value);
        if (!entry) return;
        let node = kaching ? this.kachingOption(kaching, entry) : null;
        if (!node && picker) node = this.variantTarget(entry, picker);
        const soldOut = node ? this.nodeSoldOut(node) : entry.available === false;
        this.markSoldOut(chip, soldOut);
      });
    }

    hasTarget(entry) {
      if (!entry) return false;
      if (this.variantTarget(entry)) return true;
      const selects = this.kachingSizeSelects();
      for (let i = 0; i < selects.length; i += 1) {
        if (this.kachingOption(selects[i], entry)) return true;
      }
      return false;
    }

    entryFor(value) {
      return this.data.sizes.filter((entry) => entry.value === value)[0] || null;
    }

    select(value) {
      const entry = this.entryFor(value);
      if (!entry) return;
      this.current = entry;

      this.sizesEl.querySelectorAll('.sys-size').forEach((chip) => {
        chip.setAttribute('aria-pressed', chip.dataset.value === value ? 'true' : 'false');
      });

      this.cta.textContent = entry.cta || '';
      if (this.captionEl) this.captionEl.textContent = entry.caption || '';
      if (this.noteEl) {
        this.noteEl.textContent = entry.exact === false ? this.noteEl.dataset.text || '' : '';
      }

      this.swap(entry);
    }

    swap(entry) {
      const token = ++this.swapToken;

      const paint = () => {
        if (token !== this.swapToken) return;
        if (entry.srcset) this.img.setAttribute('srcset', entry.srcset);
        else this.img.removeAttribute('srcset');
        this.img.setAttribute('src', entry.src);
        this.img.setAttribute('alt', entry.alt || '');
        if (entry.w) this.img.setAttribute('width', entry.w);
        if (entry.h) this.img.setAttribute('height', entry.h);
        this.img.style.objectPosition = entry.focal || '';
        this.img.classList.add('is-ready');
        this.painted = true;

        const settle = () => {
          if (this.mediaEl) this.mediaEl.classList.add('is-ready');
          this.maybeWarm();
        };
        if (this.img.complete && this.img.naturalWidth) settle();
        else {
          this.img.addEventListener('load', settle, { once: true });
          this.img.addEventListener('error', settle, { once: true });
        }
      };

      if (!this.painted) {
        paint();
        return;
      }

      const probe = new Image();
      probe.sizes = this.img.getAttribute('sizes') || '';
      if (entry.srcset) probe.srcset = entry.srcset;
      probe.src = entry.src;

      if (typeof probe.decode !== 'function') {
        paint();
        return;
      }

      let settled = false;
      let timer = 0;
      const done = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        paint();
      };
      timer = setTimeout(done, 2000);
      probe.decode().then(done, done);
    }

    hydrate() {
      this.refreshAvailability();

      const fromPicker = this.currentSizeFromPicker();
      const wanted =
        fromPicker ||
        (this.current && this.current.value) ||
        this.data.preselect ||
        this.data.sizes[0].value;
      this.select(this.entryFor(wanted) ? wanted : this.data.sizes[0].value);
    }

    currentSizeFromPicker() {
      const selects = this.kachingSizeSelects();
      for (let i = 0; i < selects.length; i += 1) {
        const value = String(selects[i].value).trim();
        if (this.entryFor(value)) return value;
      }

      const picker = this.variantPicker();
      if (!picker) return '';
      for (let i = 0; i < this.data.sizes.length; i += 1) {
        const entry = this.data.sizes[i];
        const node = this.variantTarget(entry, picker);
        if (!node) continue;
        const on = node.tagName === 'OPTION' ? node.selected : node.checked;
        if (on) return entry.value;
      }
      return '';
    }

    maybeWarm() {
      if (this.warmed) return;

      const conn = navigator.connection;
      if (conn) {
        if (conn.saveData) return;
        const type = conn.effectiveType || '';
        if (type && type !== '4g') return;
      } else if (this.sizeChanges < 1) {
        return;
      }

      this.warmed = true;
      const queue = this.data.sizes.filter((entry) => !this.current || entry.src !== this.current.src);
      const run = () => this.drain(queue, 2);
      if (typeof requestIdleCallback === 'function') requestIdleCallback(run, { timeout: 3000 });
      else setTimeout(run, 1200);
    }

    drain(queue, lanes) {
      const next = () => {
        const entry = queue.shift();
        if (!entry) return;
        const pre = new Image();
        const done = () => {
          pre.removeEventListener('load', done);
          pre.removeEventListener('error', done);
          next();
        };
        pre.addEventListener('load', done);
        pre.addEventListener('error', done);
        pre.sizes = this.img.getAttribute('sizes') || '';
        if (entry.srcset) pre.srcset = entry.srcset;
        pre.src = entry.src;
      };
      for (let i = 0; i < lanes; i += 1) next();
    }

    open(opener) {
      if (this.isOpen) return;
      this.isOpen = true;
      this.opener = opener || document.activeElement;

      this.hydrate();

      const hasPicker = this.hasTarget(this.current);
      const hasFallback = !!(this.current && this.current.url);
      this.cta.hidden = !hasPicker && !hasFallback;

      lockScroll(this);
      this.modal.removeAttribute('inert');
      this.modal.classList.add('is-open');
      this.setExpanded('true');

      document.addEventListener('keydown', this.onKeydown, true);

      this.card.focus({ preventScroll: true });
      if (!this.card.contains(document.activeElement)) {
        requestAnimationFrame(() => {
          if (this.isOpen && !this.card.contains(document.activeElement)) {
            this.card.focus({ preventScroll: true });
          }
        });
      }
    }

    close(silent) {
      if (!this.isOpen) return;
      this.isOpen = false;

      this.modal.classList.remove('is-open');
      this.modal.setAttribute('inert', '');
      this.setExpanded('false');
      document.removeEventListener('keydown', this.onKeydown, true);
      unlockScroll(this);

      if (silent) return;
      const back =
        this.opener && this.opener.isConnected ? this.opener : document.querySelector('[data-sys-opens]');
      if (back && typeof back.focus === 'function') back.focus({ preventScroll: true });
    }

    focusables() {
      const selector =
        'button:not([disabled]):not([hidden]), a[href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
      return Array.prototype.slice.call(this.card.querySelectorAll(selector)).filter((node) => {
        return !node.hidden && node.offsetParent !== null;
      });
    }

    handleKeydown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        event.stopPropagation();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      const items = this.focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      if (!this.card.contains(active)) {
        event.preventDefault();
        first.focus();
        return;
      }
      if (event.shiftKey && (active === first || active === this.card)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    handleSizeClick(event) {
      const chip = event.target.closest('.sys-size');
      if (!chip || !this.sizesEl.contains(chip)) return;
      if (!this.current || this.current.value !== chip.dataset.value) this.sizeChanges += 1;
      this.select(chip.dataset.value);
    }

    handleExternalChange(event) {
      if (this.applying || !this.isOpen) return;
      const node = event.target;
      if (!node || !node.tagName) return;
      if (this.modal && this.modal.contains(node)) return;

      const value = this.currentSizeFromPicker();
      if (!value || !this.entryFor(value)) return;
      if (this.current && this.current.value === value) return;
      this.select(value);
    }

    handleCta() {
      const entry = this.current;
      if (!entry) return;

      this.close();
      const applied = this.applySize(entry);

      this.dispatchEvent(
        new CustomEvent('sizeview:select', {
          bubbles: true,
          detail: {
            value: entry.value,
            optionValueId: entry.option_value_id,
            shownSize: entry.shown_size,
            applied: applied
          }
        })
      );
    }

    applySize(entry) {
      if (!entry) return false;

      this.applying = true;
      let applied = false;
      try {
        if (this.applyKaching(entry)) applied = true;
        if (this.applyDawn(entry)) applied = true;
      } finally {
        setTimeout(() => {
          this.applying = false;
        }, 0);
      }

      if (applied) return true;

      if (!entry.url) return false;
      window.location.assign(entry.url);
      return true;
    }

    applyKaching(entry) {
      const selects = this.kachingSizeSelects();
      if (!selects.length) return false;

      const want = String(entry.value).trim();
      let carried = false;

      selects.forEach((select) => {
        if (!this.kachingOption(select, entry)) return;
        carried = true;
        if (String(select.value).trim() === want) return;

        const descriptor = Object.getOwnPropertyDescriptor(window.HTMLSelectElement.prototype, 'value');
        if (descriptor && descriptor.set) descriptor.set.call(select, entry.value);
        else select.value = entry.value;

        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });

      return carried;
    }

    applyDawn(entry) {
      const target = this.variantTarget(entry);
      if (!target) return false;

      if (target.tagName === 'OPTION') {
        const select = target.closest('select');
        if (!select) return false;
        if (target.selected) return true;

        if (!select.querySelector('option[selected]')) {
          const currentOption = select.selectedOptions[0] || select.options[select.selectedIndex] || select.options[0];
          if (currentOption) currentOption.setAttribute('selected', 'selected');
        }

        target.selected = true;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }

      if (target.checked) return true;
      target.checked = true;
      target.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }
  }

  customElements.define('see-in-your-size', SeeInYourSize);
}
